import * as React from "react"
import { toast } from "sonner"
import { CircleAlert, CircleCheck, Info, Loader2, TriangleAlert, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatErrorDetail, getErrorMessage } from "@/lib/errors"

/*
  notify — feedback toasts with an optional Undo, on top of the app's sonner <Toaster/>.

    notify.success('Project “C33” deleted', { undo: () => api.post('/undo/latest') })
    notify.info('Link copied')
    notify.error('Could not save', { description: getErrorMessage(err) })   // lib/errors.js

  Options: description, undo (async fn), undoLabel ('Undo'), undoneMessage ('Change undone';
  false = no follow-up toast), duration (ms), id (replace an existing toast), onDismiss, onAutoClose.
  Toasts with an Undo stay 8 s and show a countdown; errors stay 8 s; others 5 s.
  The toast owns its timer (sonner gets duration Infinity) so the countdown and the close always
  agree: both pause while the pointer is over the toast, while it has keyboard focus, while Undo
  runs and while the browser tab is hidden.
  Returns the toast id (pass to notify.dismiss(id)).
*/

const DEFAULT_DURATION = 5000
const UNDO_DURATION = 8000
const ERROR_DURATION = 8000
const TICK_MS = 200

const VARIANTS = {
  success: { Icon: CircleCheck, iconClass: "text-[#80c804]" },
  info: { Icon: Info, iconClass: "text-[#cdcdee]" },
  warning: { Icon: TriangleAlert, iconClass: "text-[#fcd34d]" },
  error: { Icon: CircleAlert, iconClass: "text-[#fca5a5]" },
}

/** Strings, numbers and elements render as-is; FastAPI detail arrays/objects become text instead of crashing. */
function toRenderable(value) {
  if (value === null || value === undefined || value === false || value === "") return null
  if (typeof value === "string" || typeof value === "number" || React.isValidElement(value)) return value
  if (typeof value === "object") return formatErrorDetail(value) || null
  return String(value)
}

const isDocumentHidden = () => typeof document !== "undefined" && document.hidden

/** Counts `durationMs` down while not paused; calls onExpire once at 0. Returns whole seconds left. */
function useAutoDismiss(durationMs, paused, onExpire) {
  const finite = Number.isFinite(durationMs) && durationMs > 0
  const remainingRef = React.useRef(finite ? durationMs : Infinity)
  const expiredRef = React.useRef(false)
  const onExpireRef = React.useRef(onExpire)
  onExpireRef.current = onExpire
  const [seconds, setSeconds] = React.useState(finite ? Math.ceil(durationMs / 1000) : null)

  React.useEffect(() => {
    if (!finite || paused || expiredRef.current) return undefined
    let last = Date.now()
    const consume = () => {
      const now = Date.now()
      if (!isDocumentHidden()) remainingRef.current -= now - last
      last = now
    }
    const timer = setInterval(() => {
      consume()
      if (remainingRef.current <= 0) {
        clearInterval(timer)
        if (!expiredRef.current) {
          expiredRef.current = true
          onExpireRef.current?.()
        }
        return
      }
      setSeconds(Math.ceil(remainingRef.current / 1000))
    }, TICK_MS)
    return () => {
      consume()
      clearInterval(timer)
    }
  }, [finite, paused])

  return seconds
}

function NotifyToast({
  id,
  variant = "success",
  message,
  description,
  undo,
  undoLabel = "Undo",
  undoneMessage = "Change undone",
  duration = DEFAULT_DURATION,
  onExpire,
}) {
  const { Icon, iconClass } = VARIANTS[variant] || VARIANTS.info
  const [hovered, setHovered] = React.useState(false)
  const [focused, setFocused] = React.useState(false)
  const [undoing, setUndoing] = React.useState(false)
  const remaining = useAutoDismiss(duration, hovered || focused || undoing, () => {
    if (onExpire) onExpire()
    else toast.dismiss(id)
  })

  const handleUndo = async () => {
    if (undoing) return
    setUndoing(true)
    try {
      await undo()
      toast.dismiss(id)
      if (undoneMessage) notify.info(undoneMessage)
    } catch (error) {
      toast.dismiss(id)
      notify.error(getErrorMessage(error, "Could not undo the change"))
    }
  }

  const body = toRenderable(description)

  return (
    // No role here: sonner's toaster section is already the aria-live region.
    <div
      data-testid="notify-toast"
      data-variant={variant}
      data-paused={hovered || focused || undoing ? "true" : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false)
      }}
      className="flex w-[var(--width,380px)] max-w-[calc(100vw-32px)] items-center gap-3 rounded-xl bg-[color:var(--toast-bg)] py-2.5 pl-3.5 pr-2.5 text-[13px] text-white shadow-overlay"
    >
      <Icon className={cn("h-4 w-4 shrink-0", iconClass)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold leading-snug">{toRenderable(message)}</div>
        {body ? <div className="mt-0.5 text-[12px] leading-snug text-white/70">{body}</div> : null}
      </div>
      {undo ? (
        <>
          <button
            type="button"
            onClick={handleUndo}
            disabled={undoing}
            className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg bg-white/[0.12] px-2.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80c804] disabled:opacity-70"
          >
            {undoing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
            {undoing ? "Undoing…" : undoLabel}
          </button>
          {remaining !== null ? (
            <span className="w-6 shrink-0 text-right text-[12px] tabular-nums text-white/60" aria-hidden="true" data-testid="notify-countdown">
              {remaining}s
            </span>
          ) : null}
        </>
      ) : (
        <button
          type="button"
          onClick={() => toast.dismiss(id)}
          aria-label="Dismiss notification"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80c804]"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

let showSequence = 0

function show(variant, message, options = {}) {
  const {
    description,
    undo,
    undoLabel,
    undoneMessage,
    duration,
    id,
    onDismiss,
    onAutoClose,
  } = options
  const resolvedDuration = duration
    ?? (undo ? UNDO_DURATION : variant === "error" ? ERROR_DURATION : DEFAULT_DURATION)
  // A fresh key per call, so re-using an `id` restarts the countdown.
  showSequence += 1
  const key = showSequence
  let autoClosed = false
  return toast.custom(
    (toastId) => (
      <NotifyToast
        key={key}
        id={toastId}
        variant={variant}
        message={message}
        description={description}
        undo={typeof undo === "function" ? undo : undefined}
        undoLabel={undoLabel}
        undoneMessage={undoneMessage}
        duration={resolvedDuration}
        onExpire={() => {
          autoClosed = true
          onAutoClose?.({ id: toastId })
          toast.dismiss(toastId)
        }}
      />
    ),
    {
      // The toast runs its own (pausable) timer; sonner must not close it underneath the countdown.
      duration: Infinity,
      // Only pass `id` when given: sonner's toast.custom spreads these options over the id it
      // generated, so `id: undefined` would give the toast a different id than the one returned.
      ...(id !== undefined && id !== null ? { id } : {}),
      onDismiss: (data) => {
        if (!autoClosed) onDismiss?.(data)
      },
    }
  )
}

const notify = {
  success: (message, options) => show("success", message, options),
  info: (message, options) => show("info", message, options),
  warning: (message, options) => show("warning", message, options),
  error: (message, options) => show("error", message, options),
  dismiss: (id) => toast.dismiss(id),
}

export { notify, NotifyToast }
